import { type ReactNode } from 'react'

import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { matchPath } from 'react-router'

import { PageLoader } from '@/components/primitives/page-loader'
import { AppRoute } from '@/routes/routes'

import { Authentication } from './Authentication'
import {
  getAuth0Audience,
  getAuth0ClientId,
  getAuth0CookieDomain,
  getAuth0Domain
} from './auth0-config'

type AuthProviderProps = {
  children: ReactNode
}

// Keep in sync with the public route(s) registered on `publicRouter`
// (src/routes/router.tsx) — see ADR 006.
const PUBLIC_ROUTE_PATTERNS = [AppRoute.PublicDocument]

function isPublicPath(pathname: string) {
  return PUBLIC_ROUTE_PATTERNS.some(pattern => matchPath(pattern, pathname) !== null)
}

function TokenRegistrar() {
  Authentication.useRegisterToken()
  return null
}

function AuthGate({ children }: AuthProviderProps) {
  const { isLoading, error, loginWithRedirect, isAuthenticated } = useAuth0()

  if (isLoading) return <PageLoader />

  if (error) {
    return (
      <main className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">Authentication error</h1>
        <p className="text-gray-500">{error.message}</p>
      </main>
    )
  }

  if (!isAuthenticated) {
    if (isPublicPath(window.location.pathname)) return null
    loginWithRedirect()
    return null
  }

  return (
    <>
      <TokenRegistrar />
      {children}
    </>
  )
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <Auth0Provider
      domain={getAuth0Domain()}
      clientId={getAuth0ClientId()}
      cookieDomain={getAuth0CookieDomain()}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: getAuth0Audience(),
        ui_locales: navigator.language,
      }}
    >
      <AuthGate>{children}</AuthGate>
    </Auth0Provider>
  )
}
